import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, Volume2, CalendarDays, Mic } from "lucide-react";
import { Logo } from "../components/Logo";
import { decodeQrPayload, formatDateFr } from "../lib/qrPayload";

// Les enregistrements vocaux (voix réelle du professeur) sont servis depuis le
// serveur de prod (URL absolue : la page /qr peut être ouverte depuis
// n'importe quel domaine de dev/preview). Fichiers purgés après 180 jours.
const AUDIO_BASE_URL = "https://corrigeo.educform31.fr/audio-feedback/";

/**
 * Page /qr — lue par l'élève sur son téléphone après scan du QR code.
 *
 * Le feedback texte voyage dans le fragment (#) de l'URL : rien n'est envoyé
 * au serveur, rien n'est stocké (ni localStorage, ni cookie) — RGPD ok.
 * Si le payload contient `a`, la voix RÉELLE du professeur est proposée en
 * premier ; la voix de synthèse (TTS) reste disponible en secours.
 */
export default function QrView() {
  // Décodé une seule fois au montage : le hash ne change pas pendant la visite.
  const payload = useMemo(
    () => decodeQrPayload(typeof window !== "undefined" ? window.location.hash : ""),
    []
  );

  const speechOk =
    typeof window !== "undefined" && "speechSynthesis" in window;

  // idle | playing | paused
  const [speech, setSpeech] = useState("idle");
  const utterRef = useRef(null);

  // Voix réelle du professeur : `a` présent dans le payload. Si le fichier ne
  // charge pas (purgé après 180 j, format webm non lu par un vieil iPhone…),
  // on bascule visiblement vers la voix de synthèse.
  const hasVoice = Boolean(payload?.a);
  const [voiceFailed, setVoiceFailed] = useState(false);

  useEffect(() => {
    if (!speechOk) return undefined;
    // Certains navigateurs chargent les voix en différé : on les « réveille ».
    try {
      window.speechSynthesis.getVoices();
    } catch {
      /* ignore */
    }
    // Arrêt propre en quittant la page.
    return () => {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [speechOk]);

  const pickFrVoice = () => {
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      return (
        voices.find((v) => /^fr[-_]FR/i.test(v.lang)) ||
        voices.find((v) => /^fr([-_]|$)/i.test(v.lang)) ||
        null
      );
    } catch {
      return null;
    }
  };

  const toggleSpeech = () => {
    if (!speechOk || !payload) return;
    const synth = window.speechSynthesis;
    if (speech === "playing") {
      synth.pause();
      setSpeech("paused");
      return;
    }
    if (speech === "paused") {
      synth.resume();
      setSpeech("playing");
      return;
    }
    synth.cancel();
    const parts = [];
    if (payload.e) parts.push(`Correction pour ${payload.e}.`);
    if (payload.n) parts.push(`Note : ${payload.n.replace("/", " sur ")}.`);
    parts.push(payload.t);
    const u = new SpeechSynthesisUtterance(parts.join(" "));
    u.lang = "fr-FR";
    u.rate = 0.95;
    const voice = pickFrVoice();
    if (voice) u.voice = voice;
    u.onend = () => setSpeech("idle");
    u.onerror = () => setSpeech("idle");
    utterRef.current = u; // évite le garbage collection en cours de lecture
    synth.speak(u);
    setSpeech("playing");
  };

  // Quand la vraie voix est disponible, le TTS devient un secours explicite.
  const listenLabel =
    speech === "playing"
      ? "⏸ Pause"
      : speech === "paused"
      ? "▶ Reprendre l'écoute"
      : hasVoice
      ? "▶ Écouter (voix de synthèse)"
      : "▶ Écouter la correction";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="qrview-page">
      {/* En-tête : même identité que la Navbar */}
      <header className="border-b border-border bg-card/60 px-5 py-3 flex items-center justify-center">
        <Link to="/" aria-label="Accueil Copiveo">
          <Logo size={32} />
        </Link>
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-5 py-6">
        {!payload ? (
          <div
            className="rounded-2xl bg-card border border-border p-6 text-center flex flex-col items-center gap-3 mt-6"
            data-testid="qrview-error"
          >
            <QrCode className="w-10 h-10 text-muted-foreground/50" />
            <p className="font-heading font-semibold text-lg">
              Ce QR ne contient pas de feedback lisible
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le lien scanné est incomplet ou abîmé. Demande à ton professeur
              de regénérer le QR code depuis Copiveo.
            </p>
            <Link
              to="/"
              className="mt-2 text-sm font-medium text-[#1769E0] underline underline-offset-2"
            >
              Découvrir Copiveo
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl bg-card border border-border shadow-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-[#F28C28]" />
              <span className="text-sm font-medium">Ta correction</span>
              {payload.d && (
                <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {formatDateFr(payload.d)}
                </span>
              )}
            </div>

            <div className="p-5">
              {payload.e && (
                <p className="font-heading font-semibold text-xl mb-1" data-testid="qrview-student">
                  Bonjour {payload.e},
                </p>
              )}
              <p className="text-sm text-muted-foreground mb-4">
                voici le feedback de ton professeur.
              </p>

              {payload.n && (
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs text-muted-foreground">Note</span>
                  <span
                    className="font-heading font-bold text-3xl text-[#1769E0]"
                    data-testid="qrview-note"
                  >
                    {payload.n}
                  </span>
                </div>
              )}

              {hasVoice && (
                <div className="mb-4" data-testid="qrview-audio-block">
                  {!voiceFailed ? (
                    <div className="rounded-xl border border-[#F28C28]/40 bg-[#F28C28]/5 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold mb-2.5">
                        <Mic className="w-4 h-4 text-[#F28C28] flex-shrink-0" />
                        ▶ Écouter la correction de votre professeur
                      </div>
                      <audio
                        controls
                        preload="none"
                        src={`${AUDIO_BASE_URL}${encodeURIComponent(payload.a)}`}
                        className="w-full"
                        onError={() => setVoiceFailed(true)}
                        data-testid="qrview-audio-player"
                      >
                        <track kind="captions" />
                      </audio>
                      <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                        Enregistrement réel de ton professeur — conservé 180 jours
                        maximum, puis supprimé automatiquement.
                      </p>
                    </div>
                  ) : (
                    <p
                      className="text-xs text-amber-800 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 leading-relaxed"
                      data-testid="qrview-audio-fallback"
                    >
                      L'enregistrement vocal de ton professeur n'est plus
                      disponible ou n'est pas lisible sur cet appareil.
                      Tu peux écouter ta correction en voix de synthèse
                      ci-dessous.
                    </p>
                  )}
                </div>
              )}

              {speechOk ? (
                <button
                  onClick={toggleSpeech}
                  className={`w-full rounded-full active:scale-[0.99] transition font-medium px-6 flex items-center justify-center gap-2 mb-4 ${
                    hasVoice && !voiceFailed
                      ? "border border-[#F28C28]/50 text-[#F28C28] hover:bg-[#F28C28]/10 text-sm py-2.5"
                      : "bg-[#F28C28] hover:bg-[#d97a21] text-white text-base py-3.5"
                  }`}
                  data-testid="qrview-listen-btn"
                >
                  {listenLabel}
                </button>
              ) : (
                <p
                  className="text-xs text-muted-foreground bg-muted/40 rounded-xl p-3 mb-4 text-center"
                  data-testid="qrview-speech-unavailable"
                >
                  Lecture vocale non disponible sur ce navigateur.
                </p>
              )}

              <div
                className="rounded-xl bg-muted/40 border border-border p-4 text-sm leading-relaxed whitespace-pre-wrap"
                data-testid="qrview-feedback"
              >
                {payload.t}
              </div>

              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                {hasVoice
                  ? "Ce feedback écrit est contenu dans le QR code lui-même. Seul l'enregistrement vocal du professeur est hébergé en ligne (UE), accessible uniquement via ce lien et supprimé après 180 jours."
                  : "Ce feedback est contenu dans le QR code lui-même : rien n'est stocké en ligne, rien n'est envoyé à un serveur."}
              </p>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-border px-5 py-4 text-center text-[11px] text-muted-foreground">
        © 2026 EducForm31 · Karim El Gartet — Ressource pédagogique gratuite.
      </footer>
    </div>
  );
}
